import React, { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Button,
  Grid,
  Paper,
  Alert
} from '@mui/material';
import { Add as AddIcon, Person as PersonIcon, Warning as WarningIcon } from '@mui/icons-material';

import { useFormContext, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { isUserSuperAdminSelector, CurrentUserSelector } from 'app/selectors/userSelectors';
import { I18nNamespace } from '@/i18n/types/i18nNamespace';
import type { I18nScenarioEditorNs, I18nValidationNs, I18nCoreNs } from '@/i18n/dictionaries/interfaces';
import { getLanguageDisplayName } from '@/app/utils/languageUtils';
import { AccessLevel } from '@/api-gen/api.schemas';
import type { AccessLevel as AccessLevelType } from '@/api-gen/api.schemas';

import type { ScenarioSettingsType } from '../hooks/useScenarioForm';
import type { PersonaCompanyAccessInfoDto, PersonaDto, PersonaUsage } from '@/api-gen/api.schemas';
import { PersonaUsage as PersonaUsageEnum } from '@/api-gen/api.schemas';

import ScenarioPersonaCard from './ScenarioPersonaCard';
import { SharePersonaDialogContent } from './SharePersonaDialogContent';
import PersonaSelectionDialog from './PersonaSelectionDialog';
import { useScenarioPersonas } from './hooks/useScenarioPersonas';
import PersonaDetailDialog from '../../../PersonasManager/PersonaDetailDialog/PersonaDetailDialog';
import ConfirmDialog from '@/app/components/ConfirmDialog/ConfirmDialog';
import {
  usePersonaSharePersonaWithCompanies,
  getScenariosGetMissingPersonaCompanyAccessQueryOptions,
  scenariosGetMissingPersonaCompanyAccess
} from '@/api-gen/api';
import { addError, addInfo } from 'app/actions/snackbar/snackbar';

type ShareDialogState = {
  persona: PersonaDto;
  usages: PersonaUsage[];
  defaultRoleId?: number;
  missingCompanyIds: number[];
  missingCompanyNames: string[];
};


const ScenarioPersonasSettings: React.FC = () => {
  const [translate] = useTranslation([I18nNamespace.ScenarioEditor]);
  const [translateValidation] = useTranslation([I18nNamespace.Validation]);
  const [translateCore] = useTranslation([I18nNamespace.Core]);
  const { control, watch, setValue, trigger, formState: { errors, isSubmitted } } = useFormContext<ScenarioSettingsType>();
  const isSuperAdmin = useSelector(isUserSuperAdminSelector);
  const currentUser = useSelector(CurrentUserSelector);
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const [isSelectionDialogOpen, setIsSelectionDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedPersonaForEdit, setSelectedPersonaForEdit] = useState<PersonaDto | null>(null);
  const [shareDialogState, setShareDialogState] = useState<ShareDialogState | null>(null);
  const [isCheckingPersonaShare, setIsCheckingPersonaShare] = useState(false);
  const [isSharingPersona, setIsSharingPersona] = useState(false);
  const sharePersonaMutation = usePersonaSharePersonaWithCompanies();

  // Watch current scenario personas and company
  const scenarioPersonas = watch('scenarioPersonas') || [];
  const scenarioCompanyId = watch('companyId') ?? currentUser?.companyId;
  const scenarioId = watch('id');
 

  // Utility function to get user's access level to a persona
  const getUserAccessLevel = (persona: PersonaDto): AccessLevelType | undefined => {
    // Superadmins always have full access
    if (isSuperAdmin) return AccessLevel.CanAdministrate;

    // Regular users: find their company's access in the array
    const userCompanyId = currentUser?.companyId;
    if (!userCompanyId) return undefined;

    const companyAccess = persona.companies?.find(c => c.companyId === userCompanyId);
    return companyAccess?.access;
  };

  // Shared persona operations (unified across tabs)
  const { addPersona, removePersona, setDefaultPersona, setRoleId, hasDefaultPersonaError } = useScenarioPersonas(scenarioPersonas, setValue, trigger, translate);

  const handleAddPersona = () => {
    setIsSelectionDialogOpen(true);
  };

  const tryAddPersonaToScenario = (personaToAdd: PersonaDto, usages: PersonaUsage[], defaultRoleId?: number) => {
    const result = addPersona(personaToAdd, usages, defaultRoleId);
    if (!result.success) {
      console.error('Failed to add persona:', result.message);
      dispatch(
        addError({
          errorText:
            result.message ||
            translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioPersonasSettings.sharePersonaError))
        })
      );
      return false;
    }

    setIsSelectionDialogOpen(false);
    return true;
  };

  const handlePersonaSelect = async (persona: PersonaDto, usages: PersonaUsage[], defaultRoleId?: number) => {
    if (isCheckingPersonaShare || isSharingPersona) {
      return;
    }

    const personaId = persona.id;
    if (!personaId) {
      dispatch(
        addError({
          errorText: translate(
            nameof.full<I18nScenarioEditorNs>((n) => n.scenarioPersonasSettings.sharePersonaError)
          )
        })
      );
      return;
    }

    // For new scenarios (id 0 or undefined), skip sharing validation
    if (!scenarioId) {
      tryAddPersonaToScenario(persona, usages, defaultRoleId);
      return;
    }

    setIsCheckingPersonaShare(true);
    try {
      const queryOptions = getScenariosGetMissingPersonaCompanyAccessQueryOptions(
        scenarioId,
        personaId,
        {
          query: {
            staleTime: 30 * 1000
          }
        }
      );

      const response = await scenariosGetMissingPersonaCompanyAccess(scenarioId, personaId);
      queryClient.setQueryData(queryOptions.queryKey, response);
      const data = response.data;
      const missingCompanyIds = data?.missingCompanyIds ?? [];
      const missingCompanyNames = data?.missingCompanyNames ?? [];

      if (!missingCompanyIds.length) {
        tryAddPersonaToScenario(persona, usages, defaultRoleId);
        return;
      }

      setShareDialogState({
        persona,
        usages,
        defaultRoleId,
        missingCompanyIds,
        missingCompanyNames
      });
      setIsSelectionDialogOpen(false);
    } catch (error: any) {
      console.error('Failed to validate persona sharing', error);
      dispatch(
        addError({
          errorText:
            error?.message ||
            translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioPersonasSettings.sharePersonaCheckError))
        })
      );
    } finally {
      setIsCheckingPersonaShare(false);
    }
  };

  const handleShareDialogClose = () => {
    if (isSharingPersona) {
      return;
    }
    setShareDialogState(null);
  };

  const handleShareConfirm = async () => {
    if (!shareDialogState?.persona.id) {
      dispatch(
        addError({
          errorText: translate(
            nameof.full<I18nScenarioEditorNs>((n) => n.scenarioPersonasSettings.sharePersonaError)
          )
        })
      );
      setShareDialogState(null);
      return;
    }

    const personaName =
      shareDialogState.persona.name ||
      translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioPersonasSettings.unnamedPersona));

    setIsSharingPersona(true);
    try {
      await sharePersonaMutation.mutateAsync({
        id: shareDialogState.persona.id,
        data: shareDialogState.missingCompanyIds.map((companyId) => ({
          companyId,
          access: AccessLevel.CanRead
        }))
      });

      const added = tryAddPersonaToScenario(
        shareDialogState.persona,
        shareDialogState.usages,
        shareDialogState.defaultRoleId
      );

      if (added) {
        dispatch(
          addInfo({
            infoText: translate(
              nameof.full<I18nScenarioEditorNs>((n) => n.scenarioPersonasSettings.sharePersonaConfirmSuccess),
              { personaName }
            )
          })
        );
        setShareDialogState(null);
      } else {
        setShareDialogState(null);
      }
    } catch (error: any) {
      console.error('Failed to share persona with missing companies', error);
      dispatch(
        addError({
          errorText:
            error?.message ||
            translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioPersonasSettings.sharePersonaError))
        })
      );
    } finally {
      setIsSharingPersona(false);
    }
  };

  const handleRemovePersona = (personaId: number) => {
    removePersona(personaId);
  };

  const handleEditPersona = (persona: PersonaDto) => {
    setSelectedPersonaForEdit(persona);
    setIsEditDialogOpen(true);
  };

  const existingPersonaIds = scenarioPersonas
    .map(sp => sp.personaId)
    .filter((id): id is number => id != null);

  const languageMismatches = (() => {
    const scenarioLang = watch('langCode');
    if (!scenarioLang) return [] as { id: number; name: string }[];
    return scenarioPersonas
      .filter(sp => sp.persona?.languageCode && sp.persona.languageCode !== scenarioLang)
      .map(sp => ({ id: sp.personaId!, name: sp.persona?.name || `#${sp.personaId}` }));
  })();

  const shareDialogPersonaName =
    shareDialogState != null
      ? shareDialogState.persona.name ||
        translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioPersonasSettings.unnamedPersona))
      : '';

  const renderEmptyState = () => (
    <Paper
      sx={{
        p: 6,
        textAlign: 'center',
        border: '2px dashed',
        borderColor: 'divider',
        bgcolor: 'background.default'
      }}
    >
      <PersonIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
      <Typography variant="h6" color="text.secondary" gutterBottom>
        {translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioPersonasSettings.noPersonas))}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioPersonasSettings.addPersonasDescription))}
      </Typography>
      <Button
        variant="contained"
        startIcon={<AddIcon />}
        onClick={handleAddPersona}
        size="large"
      >
        {translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioPersonasSettings.addFirstPersona))}
      </Button>
    </Paper>
  );

  const renderPersonasList = () => (
    <Box>
      <Grid container spacing={3}>
        {scenarioPersonas.map((scenarioPersona) => {
          // Get the persona data from scenario or use it if embedded
          const persona = scenarioPersona.persona;

          return (
            <Grid item xs={12} sm={6} md={4} key={scenarioPersona.personaId}>
              <ScenarioPersonaCard
                scenarioPersona={scenarioPersona}
                onRemove={handleRemovePersona}
                onSetDefault={(personaId) => setDefaultPersona(personaId)}
                onEdit={handleEditPersona}
                scenarioLanguage={watch('langCode')}
                scenarioCompanyId={scenarioCompanyId}
                onChangeRoleId={(personaId, roleId) => setRoleId(personaId, roleId)}
              />
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );

  return (
    <Controller
      control={control}
      name="scenarioPersonas"
      render={() => (
        <Box sx={{ mt: 5, mb: 3, px: 3 }} >
          <Box mb={3} display={'flex'} justifyContent={'space-between'}>
            <Box >
              <Typography variant="h5" gutterBottom>
                {translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioPersonasSettings.title))}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioPersonasSettings.description))}
              </Typography>
            </Box>
            <Box mb={3} justifyContent={'flex-end'} >
              <Button
              
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddPersona}
                size="large"
              >
                {translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioPersonasSettings.addPersona))}
              </Button>
            </Box>
          </Box>




          {/* Validation Alert */}
          {isSubmitted && (hasDefaultPersonaError || languageMismatches.length > 0) && (
            <Alert
              severity="warning"
              icon={<WarningIcon />}
              sx={{ mb: 3 }}
            >
              {hasDefaultPersonaError && (
                <Box sx={{ mb: languageMismatches.length ? 1 : 0 }}>
                  {scenarioPersonas.length === 0
                    ? translateValidation(nameof.full<I18nValidationNs>((n) => n.personaValidation.noPersonasGuidance))
                    : translateValidation(nameof.full<I18nValidationNs>((n) => n.personaValidation.defaultPersonaRequiredGuidance))}
                </Box>
              )}
              {languageMismatches.length > 0 && (
                <Box>
                  {translateValidation(
                    nameof.full<I18nValidationNs>((n) => n.personaValidation.languageMismatchDetail),
                    { 
                      scenarioLanguage: getLanguageDisplayName(watch('langCode'), translateCore), 
                      personaList: languageMismatches.map(p => p.name).join(', ') 
                    }
                  )}
                </Box>
              )}
            </Alert>
          )}

          {scenarioPersonas.length === 0 ? renderEmptyState() : renderPersonasList()}

          <PersonaSelectionDialog
            open={isSelectionDialogOpen}
            onClose={() => setIsSelectionDialogOpen(false)}
            onPersonaSelect={handlePersonaSelect}
            existingPersonaIds={existingPersonaIds}
            scenarioCompanyId={scenarioCompanyId}
            scenarioLanguage={watch('langCode')}
            isProcessingSelection={isCheckingPersonaShare}
          />

          <PersonaDetailDialog
            open={isEditDialogOpen}
            onClose={() => {
              setIsEditDialogOpen(false);
              setSelectedPersonaForEdit(null);
            }}
            personaId={selectedPersonaForEdit?.id}
            onRefresh={() => {
              // No-op: optimistic path handled via onPersonaUpdated
            }}
            onPersonaUpdated={(updated) => {
              // Optimistically update form state for matching personaId
              const updatedList = (scenarioPersonas || []).map(sp =>
                sp.personaId === updated.id ? { ...sp, persona: { ...sp.persona, ...updated } } : sp
              );
              setValue('scenarioPersonas', updatedList as any);
              // Trigger validation to refresh any mismatch warnings
              trigger('scenarioPersonas');
            }}
          />
          <ConfirmDialog
            open={!!shareDialogState}
            title={
              shareDialogState
                ? translate(
                    nameof.full<I18nScenarioEditorNs>((n) => n.scenarioPersonasSettings.sharePersonaTitle),
                    { personaName: shareDialogPersonaName }
                  )
                : undefined
            }
            onConfirm={handleShareConfirm}
            onCancel={handleShareDialogClose}
            confirmButtonDisabled={isSharingPersona}
            cancelButtonDisabled={isSharingPersona}
            confirmLoading={isSharingPersona}
          >
            {shareDialogState && (
              <SharePersonaDialogContent
                persona={shareDialogState.persona}
                missingCompanyNames={
                  shareDialogState.missingCompanyNames?.length
                    ? shareDialogState.missingCompanyNames
                    : shareDialogState.missingCompanyIds.map((id) => `#${id}`)
                }
              />
            )}
          </ConfirmDialog>
        </Box>
      )}
    />
  );
};

export default ScenarioPersonasSettings;
